import { Box, type DrawerProps, IconButton, Typography } from "@mui/material";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { DialogModal } from "Components/_Shared/DialogModal";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import type { ImmovableAssetItemImmovableSubType, MovableAssetItemMovableSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useContext, useRef, useState } from "react";
import { ImmovableContractForm } from "./ImmovableContractForm";
import { MovableContractOtherForm } from "./MovableContractOtherForm";
import { MovableContractVehicleForm } from "./MovableContractVehicleForm";

type Props = {
  readonly assetContractId?: string;
  readonly assetItemId: string;
  readonly assetType: "immovable" | "movable";
  readonly assetSubType: ImmovableAssetItemImmovableSubType | MovableAssetItemMovableSubType;
  readonly contractType?: "current" | "proposed";
  readonly onClose: () => void;
} & DrawerProps;

export type SubmitContractFormHandle = {
  readonly submitForm: () => void;
  readonly delete: () => void;
};

export const ContractDrawer: FC<Props> = ({
  onClose,
  open,
  assetType,
  assetItemId,
  assetSubType,
  assetContractId,
  contractType,
  ...rest
}) => {
  const { t } = useResource();
  const { readOnly } = useContext(AnalysisDetailContext);

  const [pending, setPending] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const immovableContractFormRef = useRef<SubmitContractFormHandle>(null);
  const movableContractFormRef = useRef<SubmitContractFormHandle>(null);
  const vehicleContractFormRef = useRef<SubmitContractFormHandle>(null);

  const isVehicle = assetType === "movable" && assetSubType === "vehicle";
  const isMovableOther = assetType === "movable" && (assetSubType === "other" || assetSubType === "company");

  const activeFormRef = assetType === "immovable" ? immovableContractFormRef : isVehicle ? vehicleContractFormRef : movableContractFormRef;

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose} {...rest}>
        <Flex justifyContent="space-between" alignItems="center" padding={Spacing.XXL}>
          <Typography variant="h2">
            {t(
              assetContractId
                ? contractType === "current"
                  ? Resources.AnalysisDetail.AssetsAndContracts.EditExistingContract
                  : Resources.AnalysisDetail.AssetsAndContracts.EditNewContract
                : Resources.AnalysisDetail.AssetsAndContracts.AddContract,
            )}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Flex>
        <DrawerContent>
          <Box px={Spacing.XXL} mb={Spacing.XXL}>
            {assetType === "immovable" && (
              <ImmovableContractForm
                ref={immovableContractFormRef}
                assetContractId={assetContractId}
                assetItemId={assetItemId}
                onClose={onClose}
                onPendingChange={setPending}
              />
            )}
            {isMovableOther && (
              <MovableContractOtherForm
                ref={movableContractFormRef}
                assetItemId={assetItemId}
                assetContractId={assetContractId}
                onClose={onClose}
                onPendingChange={setPending}
              />
            )}
            {isVehicle && (
              <MovableContractVehicleForm
                ref={vehicleContractFormRef}
                assetItemId={assetItemId}
                assetContractId={assetContractId}
                onClose={onClose}
                onPendingChange={setPending}
              />
            )}
          </Box>
        </DrawerContent>
        <Flex justifyContent="space-between" pt={Spacing.XXL} pb={Spacing.XXXL} px={Spacing.XXXL}>
          <Button
            variant="text"
            color="error"
            disabled={readOnly || pending || !assetContractId}
            sx={{
              marginRight: Spacing.XL,
            }}
            onClick={() => setDeleteModalOpen(true)}
          >
            {t(Resources.Common.Delete)}
          </Button>
          <Flex flexDirection="row-reverse">
            <Button
              disabled={readOnly || pending}
              type="submit"
              onClick={() => {
                activeFormRef.current?.submitForm();
              }}
            >
              {t(Resources.Common.Save)}
            </Button>
            <Button
              onClick={onClose}
              color="secondary"
              sx={{
                marginRight: Spacing.XL,
              }}
            >
              {t(Resources.Common.Close)}
            </Button>
          </Flex>
        </Flex>
      </Drawer>
      <DialogModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteContractTitle)}
        subtitle={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteContractText)}
        primaryAction={
          <Button
            disabled={readOnly || pending}
            onClick={() => {
              setDeleteModalOpen(false);
              activeFormRef.current?.delete();
            }}
          >
            {t(Resources.Common.Yes)}
          </Button>
        }
      />
    </>
  );
};
